import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import apiClient from '../../Shared/services/api';
import { extractApiError } from '../../Shared/utils/apiError';
import { Navbar } from '../../Shared/components/Navbar';
import { LoadingSpinner } from '../../Shared/components/LoadingSpinner';
import { Button } from '../../Shared/ui/button';
import { Card } from '../../Shared/ui/card';
import { Input } from '../../Shared/ui/input';
import type {
  ActiveWorksheetResponse,
  QuestionAttemptDetail,
  SaveProgressRequest,
  SaveProgressResponse,
  WorksheetQuestion,
} from '../../Shared/types';

const WorksheetPlayer: React.FC = () => {
  const navigate = useNavigate();
  const [worksheet, setWorksheet] = useState<ActiveWorksheetResponse | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [showAnswerResult, setShowAnswerResult] = useState(false);
  const [answerResult, setAnswerResult] = useState<{
    is_correct: boolean;
    explanation: string;
    correct_answer: string;
  } | null>(null);
  const [submissionResult, setSubmissionResult] = useState<{
    score_percentage: number;
    correct_count: number;
    total_questions: number;
    message?: string;
    inspirational_quote?: string;
  } | null>(null);

  // Fire confetti when results are shown
  useEffect(() => {
    if (showResults && submissionResult) {
      // Left cannon
      void confetti({
        particleCount: 1000,
        angle: 80,
        spread: 150,
        origin: { x: 0, y: 0.8 },
        startVelocity: 100,
        shapes: ['circle', 'square', 'star'],
        colors: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'],
      });
      // Right cannon
      void confetti({
        particleCount: 1000,
        angle: 100,
        spread: 150,
        origin: { x: 1, y: 0.8 },
        startVelocity: 100,
        shapes: ['circle', 'square', 'star'],
        colors: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'],
      });
    }
  }, [showResults, submissionResult]);

  const loadWorksheet = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.getActiveWorksheet();
      setWorksheet(response);
      setCurrentIndex(response.data.current_question_index || 0);

      // Load existing answers
      const existingAnswers: Record<number, string> = {};
      response.data.question_attempts?.forEach((qa: QuestionAttemptDetail) => {
        if (qa.student_answer !== null) {
          existingAnswers[qa.question_sequence] = String(qa.student_answer);
        }
      });
      setAnswers(existingAnswers);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosErr.response?.status === 404) {
        navigate('/student/home', { replace: true });
      } else {
        setError(extractApiError(axiosErr));
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const saveProgress = useCallback(
    async (isFinal = false): Promise<SaveProgressResponse | undefined> => {
      if (!worksheet) {
        return;
      }

      setIsSaving(true);
      try {
        const payload: SaveProgressRequest = {
          current_question_index: currentIndex,
        };

        // Only send the current answer
        if (answers[currentIndex]) {
          const questions: WorksheetQuestion[] = worksheet.data.worksheet?.questions || [];
          const currentQuestion: WorksheetQuestion | undefined = questions[currentIndex];

          if (currentQuestion) {
            payload.question_attempts = [
              {
                worksheet_question_id: currentQuestion.id,
                student_answer: answers[currentIndex],
              },
            ];
          }
        }

        // Mark as final submission on last question
        if (isFinal) {
          payload.is_final = true;
        }

        const result: SaveProgressResponse = await apiClient.saveProgress(
          worksheet.data.id,
          payload
        );

        // If this was final submission, show overall results
        if (isFinal && result.score_percentage !== undefined) {
          setSubmissionResult({
            score_percentage: result.score_percentage ?? 0,
            correct_count: result.correct_count ?? 0,
            total_questions: result.total_questions ?? worksheet.data.total_questions ?? 20,
            message: result.message,
            inspirational_quote: result.inspirational_quote,
          });
          setShowResults(true);
        } else if (worksheet.type === 'practice' && result.is_correct !== undefined) {
          // Per-question feedback is shown for practice worksheets only. The diagnostic
          // is a baseline assessment and never reveals correctness mid-attempt.
          setAnswerResult({
            is_correct: result.is_correct ?? false,
            explanation: result.explanation ?? '',
            correct_answer: String(result.correct_answer ?? ''),
          });
          setShowAnswerResult(true);
        }

        return result;
      } catch (err) {
        console.error('Save failed:', err);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [worksheet, answers, currentIndex]
  );

  useEffect(() => {
    void loadWorksheet();
  }, [loadWorksheet]);

  const handleAnswerChange = (value: string): void => {
    setAnswers((prev) => ({
      ...prev,
      [currentIndex]: value,
    }));
  };

  const handleSubmit = async (): Promise<void> => {
    const questions: WorksheetQuestion[] = worksheet?.data.worksheet?.questions || [];
    const totalQuestions: number = questions.length || worksheet?.data.total_questions || 20;
    const isFinal: boolean = currentIndex === totalQuestions - 1;

    // Save and show result
    await saveProgress(isFinal);

    // The diagnostic shows no per-question feedback modal, so advance directly.
    if (!isFinal && worksheet?.type !== 'practice') {
      await handleOkayNext();
    }
  };

  const handleOkayNext = async (): Promise<void> => {
    setShowAnswerResult(false);
    setAnswerResult(null);

    const questions: WorksheetQuestion[] = worksheet?.data.worksheet?.questions || [];
    const totalQuestions: number = questions.length || worksheet?.data.total_questions || 20;
    const isLastQuestion: boolean = currentIndex === totalQuestions - 1;

    if (isLastQuestion) {
      // On last question, finalize and show completion screen
      if (worksheet) {
        try {
          const result = await apiClient.saveProgress(worksheet.data.id, {
            current_question_index: currentIndex,
            is_final: true,
          });

          if (result.score_percentage !== undefined) {
            setSubmissionResult({
              score_percentage: result.score_percentage ?? 0,
              correct_count: result.correct_count ?? 0,
              total_questions: result.total_questions ?? totalQuestions,
              message: result.message,
              inspirational_quote: result.inspirational_quote,
            });
            setShowResults(true);
          }
        } catch (err) {
          console.error('Failed to finalize worksheet:', err);
        }
      }
    } else {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);

      // Save progress with the new index so resume works correctly
      if (worksheet) {
        try {
          await apiClient.saveProgress(worksheet.data.id, {
            current_question_index: nextIndex,
            is_final: false,
          });
        } catch (err) {
          console.error('Failed to save progress after advancing:', err);
        }
      }
    }
  };

  const handleFinish = (): void => {
    if (worksheet?.type === 'diagnostic') {
      navigate('/student/home');
    } else {
      navigate('/progress');
    }
  };

  if (isLoading) {
    return (
      <Navbar>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </Navbar>
    );
  }

  if (error || !worksheet) {
    return (
      <Navbar>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card>
            <p className="text-red-600 text-center mb-4">{error || 'No active worksheet found'}</p>
            <Button onClick={() => navigate('/student/home')} className="w-full">
              Return Home
            </Button>
          </Card>
        </div>
      </Navbar>
    );
  }

  // Show results screen after submission
  // Show overall results after completing worksheet
  if (showResults && submissionResult) {
    const getScoreColor = (score: number): string => {
      if (score >= 80) {
        return 'text-green-600';
      }
      if (score >= 60) {
        return 'text-yellow-600';
      }
      return 'text-red-600';
    };

    const getScoreEmoji = (score: number): string => {
      if (score >= 90) {
        return '🎉';
      }
      if (score >= 80) {
        return '🌟';
      }
      if (score >= 70) {
        return '👍';
      }
      if (score >= 60) {
        return '💪';
      }
      return '📚';
    };

    return (
      <Navbar>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="text-center">
            <div className="mb-6">
              <div className="text-6xl mb-4">
                {getScoreEmoji(submissionResult.score_percentage)}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Worksheet Complete!</h1>
              <p className="text-gray-600">{submissionResult.message || 'Great work!'}</p>
            </div>

            {/* Inspirational Quote */}
            {submissionResult.inspirational_quote && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-6 rounded-lg mb-6">
                <p className="text-lg italic text-gray-700 text-center">
                  &ldquo;{submissionResult.inspirational_quote}&rdquo;
                </p>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Score</p>
                  <p
                    className={`text-4xl font-bold ${getScoreColor(
                      submissionResult.score_percentage
                    )}`}
                  >
                    {submissionResult.score_percentage.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Correct Answers</p>
                  <p className="text-4xl font-bold text-gray-900">
                    {submissionResult.correct_count} / {submissionResult.total_questions}
                  </p>
                </div>
              </div>
            </div>

            <Button onClick={handleFinish} className="w-full">
              {worksheet.type === 'diagnostic' ? 'Continue to Home' : 'View Progress'}
            </Button>
          </Card>
        </div>
      </Navbar>
    );
  }

  // Show answer result modal after submitting an answer
  if (showAnswerResult && answerResult) {
    return (
      <Navbar>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="text-center">
            <div className="mb-6">
              <div className="text-6xl mb-4">{answerResult.is_correct ? '✅' : '❌'}</div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {answerResult.is_correct ? 'Correct!' : 'Incorrect'}
              </h1>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-6 text-left">
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-600 mb-2">Your Answer:</p>
                <p className="text-lg text-gray-900">{answers[currentIndex]}</p>
              </div>

              {!answerResult.is_correct && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-600 mb-2">Correct Answer:</p>
                  <p className="text-lg font-bold text-green-600">{answerResult.correct_answer}</p>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-600 mb-2">Explanation:</p>
                <p className="text-gray-700">{answerResult.explanation}</p>
              </div>
            </div>

            <Button onClick={() => void handleOkayNext()} className="w-full">
              OK
            </Button>
          </Card>
        </div>
      </Navbar>
    );
  }

  // Get questions from worksheet.questions (for new worksheets) or question_attempts (for resumed worksheets)
  const questions: WorksheetQuestion[] = worksheet.data.worksheet?.questions || [];
  const currentQuestion: WorksheetQuestion | undefined = questions[currentIndex];
  const totalQuestions: number = worksheet.data.total_questions || questions.length || 20;
  const progress: number = ((currentIndex + 1) / totalQuestions) * 100;
  const isCurrentQuestionAnswered =
    answers[currentIndex] !== undefined && answers[currentIndex] !== '';

  return (
    <Navbar>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-gray-900">
              {worksheet.type === 'diagnostic' ? '🎯 Diagnostic Assessment' : '📚 Worksheet'}
            </h1>
          </div>
          {worksheet.topic && (
            <p className="text-gray-600">
              {worksheet.topic.name} - Pack {worksheet.data.pack_number}, Worksheet{' '}
              {worksheet.data.sequence_index}
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Question {currentIndex + 1} of {totalQuestions}
            </span>
            <span className="text-sm text-gray-600">{Object.keys(answers).length} answered</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <Card className="mb-6">
          {currentQuestion ? (
            <div>
              <p className="text-lg font-medium text-gray-900 mb-6">
                {currentQuestion.question_text}
              </p>

              {currentQuestion.question_type === 'multiple_choice' ? (
                <div className="space-y-3">
                  {currentQuestion.options?.map((option, idx) => (
                    <label
                      key={idx}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        answers[currentIndex] === option
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentIndex}`}
                        value={option}
                        checked={answers[currentIndex] === option}
                        onChange={(e) => handleAnswerChange(e.target.value)}
                        className="mr-3 h-4 w-4 text-blue-600"
                      />
                      <span className="text-gray-900">{option}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <Input
                  type="text"
                  value={answers[currentIndex] || ''}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isCurrentQuestionAnswered && !isSaving) {
                      void handleSubmit();
                    }
                  }}
                  placeholder="Enter your answer"
                  className="text-lg"
                />
              )}
            </div>
          ) : (
            <p className="text-gray-600">Loading question...</p>
          )}
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            onClick={() => void handleSubmit()}
            disabled={isSaving || !isCurrentQuestionAnswered}
            className="px-8"
          >
            {isSaving ? 'Submitting...' : '✓ Submit'}
          </Button>
        </div>
      </div>
    </Navbar>
  );
};

export default WorksheetPlayer;
